import { Fragment, useState } from 'react';
import {
  ChevronDown, Search, AlertCircle, Lightbulb, Wrench, X, Info, AlertTriangle,
  type LucideIcon,
} from 'lucide-react';
import {
  GUIDE_TOPICS,
  GUIDE_UI,
  type GuideLang,
  type GuideNote,
  type GuideTopic,
} from '@/lib/guide/guideContent';
import { matchGuideTopic } from '@/lib/guide/matchGuideTopic';
import './GuideHelp.scss';

// Render text with `inline code` spans: anything wrapped in backticks becomes a
// monospace chip (IPs, ports, URLs, commands). Keeps the content authorable as
// plain strings while reading like a real manual.
function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split(/(`[^`]+`)/g).map((seg, i) =>
        seg.startsWith('`') && seg.endsWith('`') ? (
          <code key={i} className="guide-code">{seg.slice(1, -1)}</code>
        ) : (
          <Fragment key={i}>{seg}</Fragment>
        ),
      )}
    </>
  );
}

const NOTE_META: Record<GuideNote['kind'], { icon: LucideIcon; label: string }> = {
  tip: { icon: Lightbulb, label: 'Tip' },
  note: { icon: Info, label: 'Note' },
  warn: { icon: AlertTriangle, label: 'Important' },
};

// Shared inner UI for the bilingual guide — used by BOTH the in-app modal
// (GuideHelp) and the standalone /guide page (Guide). Owns its own language
// state + accordion state so either container just drops it in. All copy comes
// from src/lib/guide/guideContent.ts (single source shared with the matcher).

const LANG_KEY = 'pw_guide_lang';

function initialLang(): GuideLang {
  try {
    return localStorage.getItem(LANG_KEY) === 'hi' ? 'hi' : 'en';
  } catch {
    return 'en';
  }
}

// Symptom → Cause → Fix body for a topic. Shared by the accordion AND the
// assistant result so there's one renderer to maintain.
function TopicBody({ topic, lang }: { topic: GuideTopic; lang: GuideLang }) {
  return (
    <div className="guide-topic__body">
      {topic.intro && (
        <p className="guide-topic__intro"><Rich text={topic.intro[lang]} /></p>
      )}

      {topic.symptom && (
        <div className="guide-topic__block">
          <span className="guide-topic__label guide-topic__label--symptom">
            <AlertCircle size={13} aria-hidden="true" /> {GUIDE_UI.symptomLabel[lang]}
          </span>
          <p><Rich text={topic.symptom[lang]} /></p>
        </div>
      )}

      {topic.cause && (
        <div className="guide-topic__block">
          <span className="guide-topic__label guide-topic__label--cause">
            <Lightbulb size={13} aria-hidden="true" /> {GUIDE_UI.causeLabel[lang]}
          </span>
          <p><Rich text={topic.cause[lang]} /></p>
        </div>
      )}

      {topic.steps && (
        <div className="guide-topic__block">
          <span className="guide-topic__label guide-topic__label--fix">
            <Wrench size={13} aria-hidden="true" /> {GUIDE_UI.fixLabel[lang]}
          </span>
          <ol className="guide-steps">
            {topic.steps.map((step, i) => (
              <li key={i} className="guide-steps__item">
                <span className="guide-steps__title">{step.title[lang]}</span>
                <span className="guide-steps__detail"><Rich text={step.detail[lang]} /></span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {topic.notes?.map((note, i) => {
        const meta = NOTE_META[note.kind];
        const Icon = meta.icon;
        return (
          <div key={i} className={`guide-note guide-note--${note.kind}`}>
            <Icon size={14} aria-hidden="true" className="guide-note__icon" />
            <p><strong className="guide-note__label">{meta.label}:</strong> <Rich text={note.text[lang]} /></p>
          </div>
        );
      })}

      {topic.snippet && (
        <pre className="guide-topic__snippet">
          <code>{topic.snippet}</code>
        </pre>
      )}

      {topic.table && (
        <div className="guide-topic__table-wrap">
          <table className="guide-topic__table">
            <thead>
              <tr>
                {topic.table.headers[lang].map((h, i) => (
                  <th key={i}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topic.table.rows[lang].map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Free-text "ask" box — the rule-based agent layer. Routes a question to the
// best-matching topic and renders its fix inline.
function Assistant({ lang }: { lang: GuideLang }) {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<GuideTopic | null>(null);
  const [searched, setSearched] = useState(false);

  const run = () => {
    const m = matchGuideTopic(query);
    setResult(m?.topic ?? null);
    setSearched(true);
  };

  return (
    <div className="guide-assistant">
      <label className="guide-assistant__title" htmlFor="guide-ask">
        {GUIDE_UI.askTitle[lang]}
      </label>
      <div className="guide-assistant__row">
        <input
          id="guide-ask"
          type="text"
          className="guide-assistant__input"
          placeholder={GUIDE_UI.askPlaceholder[lang]}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') run();
          }}
        />
        <button type="button" className="guide-assistant__btn" onClick={run}>
          <Search size={14} aria-hidden="true" />
          {GUIDE_UI.askButton[lang]}
        </button>
      </div>

      {searched && result && (
        <div className="guide-assistant__result">
          <p className="guide-assistant__lead">{GUIDE_UI.askMatchLead[lang]}</p>
          <h4 className="guide-assistant__match-title">{result.title[lang]}</h4>
          <TopicBody topic={result} lang={lang} />
        </div>
      )}
      {searched && !result && (
        <p className="guide-assistant__nomatch">{GUIDE_UI.askNoMatch[lang]}</p>
      )}
    </div>
  );
}

export interface GuideContentProps {
  /** Topic id to expand on mount (e.g. deep-linked from camera settings). */
  initialTopicId?: string | null;
  /** When provided (modal mode), renders a close X in the header. */
  onClose?: () => void;
}

export const GuideContent = ({ initialTopicId, onClose }: GuideContentProps) => {
  const [lang, setLang] = useState<GuideLang>(initialLang);
  // Default to the first topic (onboarding) unless a specific topic is
  // deep-linked (e.g. ?topic=fps from the camera-settings note).
  const [openId, setOpenId] = useState<string | null>(
    initialTopicId ?? GUIDE_TOPICS[0]?.id ?? null,
  );

  const switchLang = (l: GuideLang) => {
    setLang(l);
    try {
      localStorage.setItem(LANG_KEY, l);
    } catch {
      /* storage may be unavailable in some embeds — silent */
    }
  };

  return (
    <div className="guide">
      <div className="guide__head">
        <div className="guide__head-text">
          <h2 className="guide__title">{GUIDE_UI.title[lang]}</h2>
          <p className="guide__subtitle">{GUIDE_UI.subtitle[lang]}</p>
        </div>
        <div className="guide__head-actions">
          {/* Language switcher — tabs. */}
          <div className="guide__lang" role="tablist" aria-label="Language">
            <button
              type="button"
              role="tab"
              aria-selected={lang === 'en'}
              className={`guide__lang-tab${lang === 'en' ? ' guide__lang-tab--active' : ''}`}
              onClick={() => switchLang('en')}
            >
              English
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={lang === 'hi'}
              className={`guide__lang-tab${lang === 'hi' ? ' guide__lang-tab--active' : ''}`}
              onClick={() => switchLang('hi')}
            >
              हिंदी
            </button>
          </div>
          {onClose && (
            <button
              type="button"
              className="guide__close"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={18} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <Assistant lang={lang} />

      <div className="guide__topics">
        {GUIDE_TOPICS.map((topic) => {
          const expanded = openId === topic.id;
          return (
            <div
              key={topic.id}
              className={`guide-topic${expanded ? ' guide-topic--open' : ''}`}
            >
              <button
                type="button"
                className="guide-topic__header"
                aria-expanded={expanded}
                onClick={() => setOpenId(expanded ? null : topic.id)}
              >
                <span className="guide-topic__title">{topic.title[lang]}</span>
                <ChevronDown
                  size={16}
                  aria-hidden="true"
                  className="guide-topic__chevron"
                />
              </button>
              {expanded && <TopicBody topic={topic} lang={lang} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};
